import { store } from '../redux';
import { LOADING_SHOW, LOADING_HIDE } from '../redux/constant';
import {  TIMEOUT } from '../comm/config';


// --- Configuration ---
const WATCHDOG_INTERVAL = 5000; // Check every 5 seconds
const WATCHDOG_TIMEOUT = TIMEOUT + 10000; // Force hide if stuck for > 60 seconds
const REQUEST_SAFETY_TIMEOUT = TIMEOUT + 5000; // Individual request safety net (45s)

// --- Internal State ---
let loadingCount = 0;
let lastActivityTime = Date.now();
let watchdogTimerId = null;

// Map: URL -> Array of { id, timerId }
const loadingMap = new Map();

// Simple ID generator
let _reqIdCounter = 0;
const generateRequestId = () => `lg_req_${++_reqIdCounter}`;

/**
 * Internal: The Watchdog Logic
 * Checks if loading is stuck and forces a reset if necessary.
 */
const runWatchdogCheck = () => {
  const timeSinceLastActivity = Date.now() - lastActivityTime;
  if (loadingCount > 0 && timeSinceLastActivity >= WATCHDOG_TIMEOUT) {
    console.error(
      '⚠️ [LoadingGuard] CRITICAL: Loading state stuck detected! Forcing global reset.',
      { currentCount: loadingCount, timeStuck: timeSinceLastActivity }
    );

    // FORCE RESET EVERYTHING
    loadingCount = 0;
    loadingMap.clear();
    store.dispatch({ type: LOADING_HIDE });
    
    // Stop the watchdog since count is 0
    if (watchdogTimerId) {
      clearTimeout(watchdogTimerId);
      watchdogTimerId = null;
    }
  } else if (loadingCount > 0) {
    // Still loading and activity was recent, schedule next check
    watchdogTimerId = setTimeout(runWatchdogCheck, WATCHDOG_INTERVAL);
  } else {
    // No loading, stop the timer
    if (watchdogTimerId) {
      clearTimeout(watchdogTimerId);
      watchdogTimerId = null;
    }
  }
};

/**
 * Internal: Reset the watchdog timer (ping the system)
 */
const resetWatchdog = () => {
  lastActivityTime = Date.now();

  if (watchdogTimerId) {
    clearTimeout(watchdogTimerId);
  }

  // If we are loading, start/restart the check cycle
  if (loadingCount > 0) {
    watchdogTimerId = setTimeout(runWatchdogCheck, WATCHDOG_INTERVAL);
  }
};

/**
 * Public: Show Loading
 * Returns a request ID that must be passed to hideLoading later.
 */
export const showLoading = (url) => {
  const reqId = generateRequestId();

  if (loadingCount === 0) {
    store.dispatch({ type: LOADING_SHOW });
  }
  loadingCount++;

  // Set individual request safety net
  const timerId = setTimeout(() => {
    hideLoading(url, reqId, true);
  }, REQUEST_SAFETY_TIMEOUT);

  if (!loadingMap.has(url)) {
    loadingMap.set(url, []);
  }
  loadingMap.get(url).push({ id: reqId, timerId });

  // Ping the global watchdog
  resetWatchdog();

  return reqId;
};

/**
 * Public: Hide Loading
 * @param {string} url - The request URL
 * @param {string} reqId - The ID returned by showLoading
 * @param {boolean} isFromTimer - True if called by the safety timeout
 */
export const hideLoading = (url, reqId, isFromTimer = false) => {
  if (!loadingMap.has(url)) {
    // Fallback for legacy calls without ID (optional)
    if (!reqId && loadingCount > 0) {
       loadingCount--;
       if (loadingCount === 0) {
         store.dispatch({ type: LOADING_HIDE });
         if (watchdogTimerId) clearTimeout(watchdogTimerId);
       }
       resetWatchdog();
    }
    return;
  }

  const stack = loadingMap.get(url);
  
  // Find specific request entry
  const entryIndex = reqId 
    ? stack.findIndex(item => item.id === reqId)
    : 0; // Fallback to oldest if no ID provided

  if (entryIndex === -1) return; // Already cleaned up

  const entry = stack[entryIndex];

  // If called manually, clear the safety timer. If called by timer, no need.
  if (!isFromTimer) {
    clearTimeout(entry.timerId);
  }

  // Remove from stack
  stack.splice(entryIndex, 1);
  if (stack.length === 0) {
    loadingMap.delete(url);
  }

  // Decrement global count
  if (loadingCount > 0) {
    loadingCount--;
    
    // Ping watchdog
    resetWatchdog();

    if (loadingCount === 0) {
      store.dispatch({ type: LOADING_HIDE });
      // Watchdog stops automatically inside resetWatchdog when count is 0
    }
  }
};

/**
 * Optional: Get current status for debugging
 */
export const getLoadingStatus = () => ({
  count: loadingCount,
  activeUrls: Array.from(loadingMap.keys()),
  isWatchdogActive: Boolean(watchdogTimerId)
});